import { useMemo } from "react";
import { LuLoader as LuLoader2 } from "react-icons/lu";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Skeleton } from "@/components/ui/skeleton";
import { useOwnTheme } from "@/contexts/theme-context";
import { formatUSDCents } from "@/utils/currency";

interface LineGraphProps {
  earnings?: Record<string, number>;
  height?: number;
  loading?: boolean;
  colors?: string[];
  hideCharts?: boolean;
}

export const EarningsGraph = ({
  height = 300,
  earnings = {},
  loading = false,
  colors = ["#22c55d"],
  hideCharts = false,
}: LineGraphProps) => {
  const { darkTheme } = useOwnTheme();
  const chartData = useMemo(() => {
    const thisYear = new Date().getFullYear();

    return Object.entries(earnings).map(([date, value]) => {
      return {
        name: date.replace(`${thisYear}-`, ""),
        date,
        earnings: value,
      };
    });
  }, [earnings]);

  if (loading) {
    return (
      <Skeleton
        className="m-4 w-[calc(100%-2rem)]"
        style={{
          height: `calc(${height}px - 2rem)`,
        }}
      />
    );
  }

  if (hideCharts) {
    return null;
  }

  if (chartData.length === 0) {
    return (
      <div
        className="
          flex items-center justify-center p-4 text-center text-xs text-muted
        "
      >
        <LuLoader2 className="mr-2 animate-spin" />
        Loading chart...
      </div>
    );
  }

  const gridColor = darkTheme ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const tooltipBgColor = darkTheme ? "#1f2937" : "#ffffff";
  const tooltipBorderColor = darkTheme ? "#374151" : "#e5e7eb";
  const tooltipTextColor = darkTheme ? "#f9fafb" : "#111827";

  return (
    <div
      style={{
        height,
      }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colors[0]} stopOpacity={0.3} />
              <stop offset="95%" stopColor={colors[0]} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={gridColor}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: gridColor, strokeWidth: 1 }}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => formatUSDCents(value)}
            tickLine={false}
            axisLine={false}
            width={80}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: tooltipBgColor,
              color: tooltipTextColor,
              border: `1px solid ${tooltipBorderColor}`,
              borderRadius: "6px",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              padding: "8px 12px",
              fontSize: "12px",
            }}
            formatter={(value: number) => [formatUSDCents(value), "Earnings"]}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Area
            type="monotone"
            dataKey="earnings"
            stroke={colors[0]}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorEarnings)"
            activeDot={{ r: 6, strokeWidth: 0, fill: colors[0] }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
